import { useRef, useState } from "react";
import { ArrowUp, ArrowDown, ArrowUpDown, GripVertical } from "lucide-react";

/**
 * Table header cell dengan dukungan:
 * - Sort (opsional) — cycles asc → desc → default
 * - Drag & drop untuk reorder
 * - Resize handle di sisi kanan
 *
 * Semua fitur opsional; kalau `onMove` / `onResize` tidak dikasih, fitur itu OFF.
 */
export function DraggableTh({
  colKey,
  label,
  sortable = false,
  sort,
  setSort,
  width,
  onResize,           // (px:number) => void
  onMove,             // (fromKey, toKey) => void
  className = "",
  testid,
  pinSide,            // 149-at: "left" | "right" | undefined
  pinOffset,          // px offset dari edge saat sticky
}) {
  const [dragOver, setDragOver] = useState(false);
  const [resizeHover, setResizeHover] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const thRef = useRef(null);
  const resizingRef = useRef(null);

  const active = sortable && sort?.key === colKey;
  const Icon = active ? (sort.dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;

  const toggleSort = () => {
    if (!sortable || !setSort) return;
    if (!active) setSort({ key: colKey, dir: "asc" });
    else if (sort.dir === "asc") setSort({ key: colKey, dir: "desc" });
    else setSort({ key: null, dir: "asc" });
  };

  const startResize = (e) => {
    if (!onResize || !thRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = thRef.current.getBoundingClientRect().width;
    resizingRef.current = { startX, startW };
    setIsResizing(true);
    document.body.style.cursor = "col-resize";
    document.body.classList.add("select-none");
    const onMouseMove = (ev) => {
      const delta = ev.clientX - resizingRef.current.startX;
      onResize(resizingRef.current.startW + delta);
    };
    const onMouseUp = () => {
      resizingRef.current = null;
      setIsResizing(false);
      document.body.style.cursor = "";
      document.body.classList.remove("select-none");
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  const dragProps = onMove ? {
    draggable: true,
    onDragStart: (e) => { e.dataTransfer.setData("text/plain", colKey); e.dataTransfer.effectAllowed = "move"; },
    onDragOver: (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOver(true); },
    onDragLeave: () => setDragOver(false),
    onDrop: (e) => {
      e.preventDefault();
      setDragOver(false);
      const from = e.dataTransfer.getData("text/plain");
      if (from && from !== colKey) onMove(from, colKey);
    },
  } : {};

  const style = width ? { width: `${width}px`, minWidth: `${width}px`, maxWidth: `${width}px` } : {};
  // 149-at: pin sticky positioning
  if (pinSide === "left") {
    style.position = "sticky";
    style.left = `${pinOffset || 0}px`;
    style.zIndex = 22;
    style.boxShadow = "2px 0 4px -2px rgba(11, 29, 58, 0.15)";
  } else if (pinSide === "right") {
    style.position = "sticky";
    style.right = `${pinOffset || 0}px`;
    style.zIndex = 22;
    style.boxShadow = "-2px 0 4px -2px rgba(11, 29, 58, 0.15)";
  }

  return (
    <th
      ref={thRef}
      style={style}
      data-testid={testid || `th-${colKey}`}
      data-pin={pinSide || undefined}
      className={`relative select-none px-3 py-3 overline text-slate-700 whitespace-nowrap bg-white ${dragOver ? "outline outline-2 outline-[#FF5A1F]" : ""} ${className}`}
      {...dragProps}
    >
      <div className="flex items-center gap-1.5">
        {onMove && (
          <GripVertical size={11} className="text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing shrink-0" aria-hidden="true" />
        )}
        {sortable ? (
          <button
            type="button"
            data-testid={`sort-${colKey}`}
            onClick={toggleSort}
            className={`inline-flex items-center gap-1 uppercase tracking-wider text-[10px] font-semibold hover:text-[#0B1D3A] ${active ? "text-[#0B1D3A]" : "text-slate-600"}`}
          >
            {label}
            <Icon size={10} className={active ? "text-[#FF5A1F]" : "text-slate-400"} />
          </button>
        ) : (
          <span className="uppercase tracking-wider text-[10px] font-semibold text-slate-600">{label}</span>
        )}
      </div>
      {onResize && (
        <span
          data-testid={`resize-${colKey}`}
          onMouseDown={startResize}
          onMouseEnter={() => setResizeHover(true)}
          onMouseLeave={() => setResizeHover(false)}
          onDragStart={(e) => e.preventDefault()}
          className={`absolute top-1 right-0 h-[calc(100%-8px)] w-1.5 cursor-col-resize transition-colors ${isResizing ? "bg-[#FF5A1F]" : resizeHover ? "bg-[#FF5A1F]/60" : "bg-transparent hover:bg-[#FF5A1F]/40"} group`}
          title="Drag untuk resize kolom · double-click untuk fit"
        >
          {/* Visible vertical bar hover indicator — sesuai identitas orange */}
          <span className={`absolute top-1/2 right-0 -translate-y-1/2 w-[3px] h-4 transition-all ${isResizing ? "bg-[#FF5A1F] h-6" : resizeHover ? "bg-[#FF5A1F]" : "bg-slate-200 group-hover:bg-[#FF5A1F]"}`} />
        </span>
      )}
    </th>
  );
}

export default DraggableTh;
